import { useState, useEffect } from "react";
import { sbGetDocuments } from "../utils/supabase";
import S from "../styles/styles";

// ── Rent Receipt Generator ─────────────────────────────────────────────────
const generateReceipt = (payment, property, managerName = "PropDesk Property Management") => {
  const receiptNo = `RCP-${payment.id || Date.now()}`;
  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
    '<title>Rent Receipt ' + receiptNo + '</title>',
    '<style>',
    'body{font-family:Arial,sans-serif;font-size:13px;color:#1a1a1a;margin:0;padding:40px;background:#fff}',
    '.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:20px;border-bottom:3px solid #E8622A}',
    '.logo{font-size:26px;font-weight:800;letter-spacing:-0.5px}',
    '.logo span{color:#E8622A}',
    '.receipt-meta{text-align:right}',
    '.receipt-title{font-size:22px;font-weight:800;color:#1a1a1a;margin-bottom:4px}',
    '.receipt-no{font-size:13px;color:#666}',
    '.paid-stamp{display:inline-block;border:3px solid #22AA55;color:#22AA55;font-weight:800;font-size:16px;padding:4px 16px;border-radius:4px;transform:rotate(-5deg);margin-top:8px;letter-spacing:2px}',
    '.section{margin-bottom:20px}',
    '.section-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#999;margin-bottom:8px}',
    '.row{display:flex;gap:40px;margin-bottom:16px}',
    '.party{flex:1}',
    '.party-name{font-size:15px;font-weight:700;margin-bottom:4px}',
    '.party-detail{font-size:12px;color:#666;line-height:1.7}',
    '.amount-box{background:#1a1a1a;color:#fff;border-radius:10px;padding:20px 28px;margin:20px 0;display:flex;justify-content:space-between;align-items:center}',
    '.amount-label{font-size:12px;color:#aaa;margin-bottom:4px}',
    '.amount-value{font-size:32px;font-weight:800;color:#E8622A}',
    '.details-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px}',
    '.detail-item{background:#f8f8f8;border-radius:8px;padding:12px 16px}',
    '.detail-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#999;margin-bottom:4px}',
    '.detail-value{font-size:13px;font-weight:600;color:#1a1a1a}',
    '.legal{font-size:11px;color:#666;line-height:1.6;border-top:1px solid #eee;padding-top:16px;margin-top:20px}',
    '.footer{text-align:center;font-size:11px;color:#aaa;margin-top:24px}',
    '@media print{body{padding:20px}button{display:none}}',
    '</style></head><body>',
    '<div class="header">',
    '<div><div class="logo">prop<span>desk</span></div>',
    '<div style="font-size:12px;color:#666;margin-top:4px">Property Management Services</div></div>',
    '<div class="receipt-meta">',
    '<div class="receipt-title">RENT RECEIPT</div>',
    '<div class="receipt-no">' + receiptNo + '</div>',
    '<div class="paid-stamp">PAID</div>',
    '</div></div>',
    '<div class="row">',
    '<div class="party"><div class="section-title">Received From</div>',
    '<div class="party-name">' + (payment.renterName || '') + '</div>',
    '<div class="party-detail">Unit ' + (payment.unit || '') + '<br/>' + (property?.address || property?.name || '') + '<br/>' + (property?.city || 'Vancouver, BC') + '</div></div>',
    '<div class="party"><div class="section-title">Received By</div>',
    '<div class="party-name">' + managerName + '</div>',
    '<div class="party-detail">propdesk.ca<br/>Vancouver, BC</div></div>',
    '</div>',
    '<div class="amount-box">',
    '<div><div class="amount-label">Amount Received</div>',
    '<div class="amount-value">$' + parseFloat(payment.amount || 0).toLocaleString("en-CA", {minimumFractionDigits:2}) + '</div></div>',
    '<div style="text-align:right"><div style="font-size:12px;color:#aaa;margin-bottom:4px">Payment Date</div>',
    '<div style="font-size:18px;font-weight:700;color:#fff">' + (payment.date || '') + '</div></div>',
    '</div>',
    '<div class="details-grid">',
    '<div class="detail-item"><div class="detail-label">For Period</div><div class="detail-value">' + (payment.notes || 'Rent Payment') + '</div></div>',
    '<div class="detail-item"><div class="detail-label">Payment Method</div><div class="detail-value">' + (payment.method || 'e-Transfer') + '</div></div>',
    '<div class="detail-item"><div class="detail-label">Property</div><div class="detail-value">' + (property?.address || property?.name || '—') + '</div></div>',
    '<div class="detail-item"><div class="detail-label">Unit</div><div class="detail-value">Unit ' + (payment.unit || '—') + '</div></div>',
    '</div>',
    '<div class="legal">',
    'This receipt confirms that rent payment of <strong>$' + parseFloat(payment.amount || 0).toFixed(2) + '</strong> ',
    'has been received from <strong>' + (payment.renterName || 'Tenant') + '</strong> ',
    'for the rental unit at <strong>' + (property?.address || 'the above address') + '</strong>, ',
    'Unit <strong>' + (payment.unit || '') + '</strong>, Vancouver, BC.<br/><br/>',
    'This receipt is generated electronically and is valid without a physical signature under the BC Residential Tenancy Act.',
    '</div>',
    '<div class="footer">Generated by PropDesk · propdesk.ca · ' + new Date().toLocaleDateString("en-CA") + '</div>',
    '</body></html>',
  ].join('\n');

  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = receiptNo + '_' + (payment.renterName || 'receipt').replace(/\s+/g,'_') + '.html';
  a.click();
  URL.revokeObjectURL(url);
};
import { Logo, StatCard, EmptyState, categoryIcon, PriorityBadge, StatusBadge } from "./UI";
import TicketForm from "./TicketForm";
import ChexyButton from "./ChexyButton";

const RenterPortal = ({ currentUser, properties, tickets, payments = [], documents = [], onAddTicket, onSignOut, onGetComments, onRefreshPayments }) => {
  const [showForm,   setShowForm]   = useState(false);
  const [localDocs,  setLocalDocs]  = useState([]);
  const [activeTab,  setActiveTab]  = useState("maintenance");
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [debugMsg, setDebugMsg] = useState("");
  const [ticketComments, setTicketComments] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Fetch documents directly for renter
  useEffect(() => {
    if (!currentUser?.id) return;
    sbGetDocuments()
      .then((docs) => {
        const mine = docs.filter((d) => String(d.renterId) === String(currentUser.id));
        if (mine.length) setLocalDocs(mine);
      })
      .catch(() => {});
  }, [currentUser?.id]);

  const myProperty    = properties.find((p) => p.id === currentUser?.propertyId);
  const allDocs = [...documents, ...localDocs.filter((d) => !documents.find((x) => x.id === d.id))];
  const myDocuments   = allDocs.filter((d) => String(d.renterId) === String(currentUser?.id));
  const myTickets     = tickets.filter((t) => t.renterId === currentUser?.id);
  const myReceipts    = payments.filter((p) =>
    p.type === "rent_received" && (
      (p.renterName && currentUser?.name && p.renterName.toLowerCase() === currentUser.name.toLowerCase()) ||
      (p.unit && currentUser?.unit && String(p.unit) === String(currentUser.unit) &&
       String(p.propertyId) === String(currentUser.propertyId))
    )
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

  const stats = [
    { label: "My Open",     value: myTickets.filter((t) => t.status === "OPEN").length,        color: "#FF2D2D" },
    { label: "In Progress", value: myTickets.filter((t) => t.status === "IN_PROGRESS").length, color: "#2A7BE8" },
    { label: "Resolved",    value: myTickets.filter((t) => t.status === "RESOLVED").length,    color: "#22AA55" },
  ];

  return (
    <div style={S.body}>
      <div style={S.renterWrap}>

        {/* Header */}
        <div style={S.renterHeader}>
          <Logo />
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#9993A8" }}>Hi, {currentUser?.name}</span>
            <button style={{ ...S.btnGhost, fontSize: 12, border: "1px solid #ffffff15", borderRadius: 8, padding: "5px 12px" }} onClick={onSignOut}>Sign Out</button>
          </div>
        </div>

        <div style={S.renterCard}>
          {/* Page header */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 4 }}>
              <div style={S.pageTitle}>{myProperty?.address || "My Portal"}</div>
              {activeTab === "maintenance" && (
                <button style={{ ...S.btnPrimary, fontSize: 13, padding: "10px 18px", whiteSpace: "nowrap" }} onClick={() => setShowForm(true)}>
                  + New Request
                </button>
              )}
            </div>
            <div style={{ fontSize: 13, color: "#9993A8" }}>
              {myProperty ? `Unit ${currentUser?.unit}` : "No property assigned"}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ ...S.tabRow, marginBottom: 24 }}>
            <button style={{ ...S.tabBtn, ...(activeTab === "maintenance" ? S.tabBtnActive : {}) }} onClick={() => setActiveTab("maintenance")}>
              Maintenance ({myTickets.length})
            </button>
            <button
              style={{ ...S.tabBtn, ...(activeTab === "receipts" ? S.tabBtnActive : {}) }}
              onClick={() => { setActiveTab("receipts"); onRefreshPayments?.(); }}
            >
              Rent Receipts ({myReceipts.length})
            </button>
            <button style={{ ...S.tabBtn, ...(activeTab === "documents" ? S.tabBtnActive : {}) }} onClick={() => setActiveTab("documents")}>
              My Documents ({myDocuments.length})
            </button>
          </div>

          {/* Maintenance Tab */}
          {activeTab === "maintenance" && <>

          {/* Stats */}
          <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            {stats.map((st) => (
              <div key={st.label} style={{ flex: 1, minWidth: 100, background: "#0F0F14", borderRadius: 10, padding: "14px 16px", border: "1px solid #ffffff08" }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: st.color }}>{st.value}</div>
                <div style={{ fontSize: 11, color: "#9993A8", fontWeight: 600, marginTop: 2 }}>{st.label}</div>
              </div>
            ))}
          </div>

          {/* Ticket list */}
          <div style={{ fontSize: 13, color: "#9993A8", marginBottom: 14, fontWeight: 700 }}>
            MY SUBMITTED REQUESTS
          </div>

          {myTickets.length === 0
            ? <EmptyState icon="📭" title="No requests yet" subtitle="Submit your first maintenance request above" />
            : myTickets.map((t) => (
              <div key={t.id} style={{ borderBottom: "1px solid #ffffff08" }}>
                {/* Ticket row -- tap to expand */}
                <div style={{ padding: "14px 0", display: "flex", gap: 12, alignItems: "center", cursor: "pointer" }}
                  onClick={async () => {
                    if (expandedTicket === t.id) {
                      setExpandedTicket(null);
                      setTicketComments([]);
                    } else {
                      setExpandedTicket(t.id);
                      if (onGetComments) {
                        try {
                          const cmts = await onGetComments(t.id);
                          setTicketComments(cmts);
                          setDebugMsg(`Ticket ${t.id}: ${cmts.length} comments loaded`);
                        } catch(err) {
                          setDebugMsg(`Error: ${err.message}`);
                        }
                      } else {
                        setDebugMsg("onGetComments not available");
                      }
                    }
                  }}>
                  <div style={{ width: 38, height: 38, borderRadius: 8, background: "#ffffff08", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                    {categoryIcon(t.category)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{t.title}</div>
                    <div style={{ fontSize: 11, color: "#9993A8", display: "flex", gap: 8, alignItems: "center" }}>
                      <span>{t.category}</span>
                      <span>.</span>
                      <span>{t.date}</span>
                      {t.photos?.length > 0 && <span>. 📷 {t.photos.length}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <StatusBadge   status={t.status}   />
                    <PriorityBadge priority={t.priority} />
                    <span style={{ fontSize: 10, color: "#5A5570" }}>{expandedTicket === t.id ? "▲" : "▼"}</span>
                  </div>
                </div>

                {/* Expanded comments thread */}
                {expandedTicket === t.id && (
                  <div style={{ background: "#0F0F14", borderRadius: 10, padding: 14, marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      Updates from your Property Manager
                    </div>
                    <div style={{ fontSize: 11, color: "#FF7A22", marginBottom: 8 }}>{debugMsg}</div>
                    {ticketComments.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#5A5570", fontStyle: "italic" }}>No updates yet.</div>
                    ) : (
                      ticketComments.map((c) => (
                        <div key={c.id} style={{ background: "#141418", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#E8622A" }}>{c.authorName}</span>
                            <span style={{ fontSize: 10, color: "#5A5570" }}>
                              {new Date(c.createdAt).toLocaleDateString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "#D0CDD8", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{c.message}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))
          }

          </>}

          {/* Documents Tab */}
          {activeTab === "documents" && (
            <div>
              {myDocuments.length === 0 ? (
                <div style={{ padding: "48px 24px", textAlign: "center" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>📁</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No documents yet</div>
                  <div style={{ fontSize: 13, color: "#9993A8" }}>Your property manager will upload your lease and inspection reports here</div>
                </div>
              ) : (
                <div>
                  {myDocuments.map((doc) => {
                    const icons = {
                      "Lease Agreement": "📄", "Move-In Inspection": "🔍",
                      "Move-Out Inspection": "🔍", "ID / Verification": "🪪",
                      "Insurance Certificate": "🛡", "Building Permit": "🏗",
                      "Strata Document": "🏢", "Other": "📎",
                    };
                    return (
                      <div key={doc.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: "1px solid #ffffff08" }}>
                        <div style={{ fontSize: 28, flexShrink: 0 }}>{icons[doc.type] || "📎"}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{doc.name}</div>
                          <div style={{ fontSize: 11, color: "#9993A8" }}>
                            {doc.type} {doc.uploadedAt ? ". " + new Date(doc.uploadedAt).toLocaleDateString("en-CA") : ""}
                          </div>
                          {doc.notes && <div style={{ fontSize: 11, color: "#5A5570", marginTop: 2 }}>{doc.notes}</div>}
                        </div>
                        {doc.fileUrl && (
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ ...S.btnSmall, background: "#2A7BE818", color: "#2A7BE8", fontSize: 12, padding: "6px 14px", textDecoration: "none", borderRadius: 8 }}
                          >
                            View
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── RECEIPTS TAB ── */}
        {activeTab === "receipts" && (
          <div>
            {/* Header row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>Rent Receipts</div>
                <div style={{ fontSize: 12, color: "#9993A8" }}>Download official receipts for your records or proof of payment</div>
              </div>
              <button
                style={{ ...S.btnSmall, background: "#ffffff10", color: "#9993A8", fontSize: 12, padding: "8px 14px", borderRadius: 8, display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => onRefreshPayments?.()}
              >
                Refresh
              </button>
            </div>

            {/* Summary strip */}
            {myReceipts.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
                {[
                  { label: "Total Paid", value: `$${myReceipts.reduce((s, r) => s + parseFloat(r.amount || 0), 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`, color: "#22AA55" },
                  { label: "Receipts",   value: myReceipts.length,                                                                                                                  color: "#E8622A" },
                  { label: "Latest",     value: myReceipts[0]?.date || "—",                                                                                                         color: "#2A7BE8" },
                ].map((s) => (
                  <div key={s.label} style={{ background: "#0F0F14", border: "1px solid #ffffff08", borderRadius: 12, padding: "14px 16px", textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.color, marginBottom: 4 }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: "#5A5570", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Receipts list */}
            {myReceipts.length === 0 ? (
              <div style={{ padding: "56px 24px", textAlign: "center", background: "#0F0F14", borderRadius: 14, border: "1px dashed #ffffff15" }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>🧾</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>No receipts yet</div>
                <div style={{ fontSize: 13, color: "#9993A8", maxWidth: 280, margin: "0 auto", lineHeight: 1.6 }}>
                  Your rent receipts will appear here as soon as your manager posts your monthly payment
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {myReceipts.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      background: "#0F0F14",
                      border: "1px solid #ffffff08",
                      borderRadius: 14,
                      padding: "16px 18px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    {/* Icon */}
                    <div style={{
                      width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                      background: "#22AA5518", border: "1px solid #22AA5530",
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
                    }}>
                      🧾
                    </div>

                    {/* Details — grows to fill space */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 16, fontWeight: 800, color: "#F0EDE8" }}>
                          ${parseFloat(r.amount || 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 99,
                          background: "#22AA5518", color: "#22AA55", letterSpacing: "0.05em",
                          textTransform: "uppercase",
                        }}>
                          PAID
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#9993A8" }}>
                        {r.notes || "Rent Payment"} &nbsp;·&nbsp; {r.date}
                      </div>
                    </div>

                    {/* Buttons — right aligned, vertically centered */}
                    <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                      <button
                        style={{
                          background: "#E8622A", color: "#fff", border: "none",
                          borderRadius: 8, padding: "8px 16px",
                          fontSize: 13, fontWeight: 700, cursor: "pointer",
                        }}
                        onClick={() => generateReceipt(r, myProperty)}
                      >
                        Download
                      </button>
                      <button
                        style={{
                          background: "#ffffff08", color: "#9993A8",
                          border: "1px solid #ffffff10", borderRadius: 8,
                          padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                        }}
                        onClick={() => { generateReceipt(r, myProperty); window.print(); }}
                      >
                        Print
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Pay rent with Chexy */}
            <div style={{ marginTop: 24, borderTop: "1px solid #ffffff08", paddingTop: 20 }}>
              <div style={{ fontSize: 12, color: "#9993A8", marginBottom: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Pay Rent Online
              </div>
              <ChexyButton
                renterName={currentUser?.name}
                propertyAddress={myProperty?.address}
                unit={currentUser?.unit}
              />
            </div>
          </div>
        )}
      </div>

      {/* Ticket submission form */}
      {showForm && (
        <TicketForm
          currentUser={currentUser}
          properties={properties}
          tickets={tickets}
          onSubmit={onAddTicket}
          onClose={() => setShowForm(false)}
          isManager={false}
        />
      )}
    </div>
  );
};

export default RenterPortal;
